// <-- Core Imports -->
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";  
import i18next from "i18next";
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function calculateReadingTime(text: string) {
  const wordsPerMinute = 200; // Average reading speed
  const textLength = text.split(/\s+/).length; // Split by spaces to calculate number of words
  if (textLength === 0) {
    return "0 min read";
  }

  const minutes = Math.ceil(textLength / wordsPerMinute);
  return `${minutes} min read`;
}

export function formatNumber(num: number) {
  if (num >= 1000000) {
    const formatted = (num / 1000000).toFixed(2); // Two decimal places
    // Remove trailing zeroes
    return "$" + parseFloat(formatted).toString() + "M";
  } else if (num >= 1000) {
    const formatted = (num / 1000).toFixed(2); // Two decimal places
    // Remove trailing zeroes
    return "$" + parseFloat(formatted).toString() + "K";
  } else {
    return "$" + (num?.toString() || "0");
  }
}

export function getCalculatedTimeAgo(time: string | number | Date): string | null {
  const tPlural = (key: string, count: number) =>
    i18next.t(count === 1 ? key : `${key}_plural`, { count });

  const currentDate = new Date().getTime();
  const createdDate = new Date(time).getTime();

  const diff = currentDate - createdDate;

  const seconds = Math.round(diff / 1000);
  const minutes = Math.round(diff / (1000 * 60));
  const hours = Math.round(diff / (1000 * 60 * 60));
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  const weeks = Math.round(diff / (1000 * 60 * 60 * 24 * 7));
  const months = Math.round(diff / (1000 * 60 * 60 * 24 * 30));
  const years = Math.round(diff / (1000 * 60 * 60 * 24 * 365));

  if (years > 0) {
    return tPlural("time_ago.posted_years_ago", years);
  } else if (months > 0) {
    return tPlural("time_ago.posted_months_ago", months);
  } else if (weeks > 0) {
    return tPlural("time_ago.posted_weeks_ago", weeks);
  } else if (days > 1) {
    return tPlural("time_ago.posted_days_ago", days);
  } else if (days === 1) {
    return i18next.t("time_ago.posted_one_day_ago");
  } else if (hours > 0) {
    return tPlural("time_ago.posted_hours_ago", hours);
  } else if (minutes > 0) {
    return tPlural("time_ago.posted_minutes_ago", minutes);
  } else if (seconds >= 0) {
    return i18next.t("time_ago.just_now");
  }

  return i18next.t("time_ago.just_now");
}



export function getAvatarFallbackText(name: string): string {
  if (!name) return "";

  const parts = name.trim().split(/\s+/); // handles multiple spaces
  if (parts.length === 0) return "";

  const first = parts[0]?.[0]?.toUpperCase() || "";
  const second = parts[1]?.[0]?.toUpperCase() || "";

  return first + second;
}

export function getDaysAgo(time: string | number | Date): string | null {
  const currentDate = new Date().getTime();
  const createdDate = new Date(time).getTime();

  const diff = currentDate - createdDate;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days > 1) {
    return `${days} days ago`;
  } else if (days === 1) {
    return `one day ago`;
  } else if (days === 0) {
    return `today`;
  }

  return null;
}

export function truncateFileName(fileName: string, maxLength: number = 30): string {
  if (fileName.length <= maxLength) return fileName;

  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    // No extension, just truncate
    return fileName.substring(0, maxLength - 3) + '...';
  }

  const extension = fileName.substring(lastDotIndex);
  const nameWithoutExt = fileName.substring(0, lastDotIndex);
  const availableLength = maxLength - extension.length - 3; // 3 for '...'

  if (availableLength <= 0) {
    // Extension is too long, just show it
    return '...' + extension;
  }

  return nameWithoutExt.substring(0, availableLength) + '...' + extension;
}
